/**
 * Montaje del modal de la radio en el DOM.
 *
 * Diseño:
 *  - El modal se monta al llamar a `mountRadio()` (típicamente en init).
 *  - Existe en el DOM siempre, pero invisible (transparente, pointer-events:none)
 *    hasta que se le aplica la clase `.r-open`.
 *  - Esto coincide con el comportamiento del legacy: el modal está siempre
 *    en el DOM, las animaciones de entrada/salida son sólo CSS sobre `.r-open`.
 *
 * En Fase 4b-3 conectaremos `mountRadio()` con `init()` del módulo y se
 * llamará desde el index.html sustituyendo a /legacy/radio.js.
 *
 * En Fase 4b-1 (esta) sólo proporcionamos los building blocks; el modal
 * SÓLO se muestra si se llama explícitamente a `mountRadio()` y `openRadio()`
 * desde la consola, para verificación.
 */

import { buildModal } from './markup.js';
import { setOpen, state } from './state.js';
// Import estático del CSS — alinea con el resto de módulos del proyecto.
// El dinámico anterior (`void ensureCss()`) generaba un race: al volver a
// la sala, el JS añadía el overlay al DOM antes de que llegara el CSS, y
// el modal se renderizaba con estilos por defecto del browser (opacity:1,
// transform:identidad) hasta que el CSS de la radio aplicaba su
// `opacity:0` con transición — produciendo un fade-out visible del
// modal en cada navegación.
import './radio.css';

/** Referencia al overlay raíz una vez montado. null si no está montado. */
let overlay: HTMLDivElement | null = null;

/**
 * Monta el modal en `document.body` si no estaba ya montado. Idempotente:
 * llamarlo varias veces no duplica el DOM.
 *
 * @returns El overlay raíz, listo para que UI/listeners lo manipulen.
 */
export function mountRadio(): HTMLDivElement {
  if (overlay && overlay.isConnected) return overlay;
  overlay = buildModal();
  document.body.appendChild(overlay);
  return overlay;
}

/**
 * Desmonta el modal del DOM. Útil en tests; en producción no se desmonta:
 * el modal vive durante toda la sesión.
 */
export function unmountRadio(): void {
  if (overlay && overlay.isConnected) {
    overlay.remove();
  }
  overlay = null;
}

/** Devuelve el overlay actualmente montado (null si no lo está). */
export function getOverlay(): HTMLDivElement | null {
  return overlay;
}

/**
 * Abre el modal: añade la clase `.r-open` que dispara las transiciones
 * CSS de fondo, blur y entrada del modal interior. También actualiza
 * el estado.
 *
 * Requisito: el modal debe estar montado (llamar a mountRadio() antes).
 */
export function openRadio(): void {
  if (!overlay) return;
  overlay.classList.add('r-open');
  setOpen(true);
}

/**
 * Cierra el modal: quita la clase `.r-open` y revierte el estado.
 */
export function closeRadio(): void {
  if (!overlay) return;
  overlay.classList.remove('r-open');
  setOpen(false);
}

/** Devuelve si el modal está abierto en este momento. */
export function isRadioOpen(): boolean {
  return state.isOpen;
}
